import React, { useEffect, useState, useRef } from 'react'
import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import {
    Add,
    Close,
    Edit,
    FilterList,
    Search,
} from '@mui/icons-material'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { api } from '../../../../services/Main/Api'
import { getCondoId } from '../../../../services/Main/AuthStorage'
import { formatCpfCnpj, formatPhone, stripNonDigits } from '../../../../helpers/formatters'
import NoResultsAnimation from '../../../../components/Shared/animations/NoResultsAnimation'
import PaginationGlobal from '../../../../components/Shared/PaginationGlobal'
import { useSnackbar } from 'notistack'

const CONTACT_TYPE_OPTIONS = [
    { key: null, label: 'Todos', color: '#64748B' },
    { key: 'CLIENT', label: 'Cliente', color: '#3B82F6' },
    { key: 'EMPLOYEE', label: 'Funcionário', color: '#8B5CF6' },
    { key: 'SUPPLIER', label: 'Fornecedor', color: '#F59E0B' },
    { key: 'BUSINESS_PARTNER', label: 'Sócio', color: '#22C55E' },
]

const getTypeChip = (typeKey) => {
    const found = CONTACT_TYPE_OPTIONS.find((t) => t.key === typeKey)
    if (!found) return null
    return (
        <Chip
            label={found.label}
            size="small"
            sx={{
                bgcolor: `${found.color}14`,
                color: found.color,
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 26,
                border: `1px solid ${found.color}30`,
            }}
        />
    )
}

const STATES = [
    'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
    'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
]

const INITIAL_FORM = {
    name: '',
    cpf: '',
    email: '',
    phone: '',
    contact_type: 'CLIENT',
    cep: '',
    street: '',
    address_number: '',
    complement: '',
    district: '',
    city: '',
    state: '',
}

const ContactsList = () => {
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const typingTimerRef = useRef(null)

    const [response, setResponse] = useState(null)
    const [activeType, setActiveType] = useState(null)
    const [search, setSearch] = useState(null)
    const [loading, setLoading] = useState(true)
    const [filters, setFilters] = useState({
        condo_id: getCondoId(),
        page_size: 50,
        page: 1,
    })

    const [dialogOpen, setDialogOpen] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [editId, setEditId] = useState(null)
    const [form, setForm] = useState(INITIAL_FORM)
    const [errors, setErrors] = useState({})
    const [saving, setSaving] = useState(false)
    const [cepLoading, setCepLoading] = useState(false)

    const handleCepBlur = (e) => {
        const cep = stripNonDigits(e.target.value)
        if (cep.length !== 8) return
        setCepLoading(true)
        axios.get(`https://viacep.com.br/ws/${cep}/json/`)
            .then((res) => {
                if (!res.data.erro) {
                    setForm((prev) => ({
                        ...prev,
                        street: res.data.logradouro || prev.street,
                        district: res.data.bairro || prev.district,
                        city: res.data.localidade || prev.city,
                        state: res.data.uf || prev.state,
                        complement: res.data.complemento || prev.complement,
                    }))
                }
            })
            .catch(() => {})
            .finally(() => setCepLoading(false))
    }

    function getContacts() {
        const params = {
            page_size: filters.page_size,
            page: filters.page,
            condo_id: getCondoId(),
        }

        if (activeType) params.contact_type = activeType
        if (search) params.search = search

        api.get('unit/', { params })
            .then((res) => setResponse(res.data))
            .catch((error) => console.log(error))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        getContacts()
        window.scrollTo(0, 0)
    }, [filters, activeType, search])

    const openCreateDialog = () => {
        setForm(INITIAL_FORM)
        setEditMode(false)
        setEditId(null)
        setErrors({})
        setDialogOpen(true)
    }

    const openEditDialog = (contact) => {
        setForm({
            name: contact.name || '',
            cpf: contact.cpf || '',
            email: contact.email || '',
            phone: contact.phone || '',
            contact_type: contact.contact_type || 'CLIENT',
            cep: contact.cep || '',
            street: contact.street || '',
            address_number: contact.address_number || '',
            complement: contact.complement || '',
            district: contact.district || '',
            city: contact.city || '',
            state: contact.state || '',
        })
        setEditMode(true)
        setEditId(contact.id)
        setErrors({})
        setDialogOpen(true)
    }

    const handleSave = () => {
        const newErrors = {}
        if (!form.name.trim()) newErrors.name = 'Nome é obrigatório'
        if (!form.cpf.trim()) newErrors.cpf = 'CPF/CNPJ é obrigatório'

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        setSaving(true)
        const data = {
            ...form,
            cpf: stripNonDigits(form.cpf),
            phone: form.phone ? stripNonDigits(form.phone) : null,
            condo: getCondoId(),
        }

        const request = editMode
            ? api.put(`unit/${editId}/`, data)
            : api.post('unit/', data)

        request
            .then(() => {
                enqueueSnackbar(
                    editMode ? 'Contato atualizado com sucesso' : 'Contato criado com sucesso',
                    { variant: 'success' }
                )
                setDialogOpen(false)
                getContacts()
            })
            .catch((err) => {
                const resErrors = err?.response?.data
                if (resErrors && typeof resErrors === 'object') {
                    const mapped = {}
                    Object.entries(resErrors).forEach(([key, val]) => {
                        mapped[key] = Array.isArray(val) ? val.join(', ') : val
                    })
                    setErrors(mapped)
                } else {
                    enqueueSnackbar('Erro ao salvar contato', { variant: 'error' })
                }
            })
            .finally(() => setSaving(false))
    }

    const cellSx = {
        color: '#94A3B8',
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #E2E8F0',
    }

    return (
        <>
            <Helmet>
                <title>Contatos - Bloco B3</title>
            </Helmet>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="xl" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            Contatos
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={openCreateDialog}
                            sx={{
                                bgcolor: '#3B82F6',
                                '&:hover': { bgcolor: '#2563EB' },
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 3,
                            }}
                        >
                            Novo Contato
                        </Button>
                    </Box>

                    {/* Filters Card */}
                    <Card
                        sx={{
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            mb: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FilterList sx={{ color: '#94A3B8', fontSize: 20 }} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#94A3B8',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        fontSize: '0.7rem',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Filtros
                                </Typography>
                            </Box>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        placeholder="Buscar por nome, CPF/CNPJ ou email..."
                                        variant="outlined"
                                        size="small"
                                        onKeyUp={(event) => {
                                            clearTimeout(typingTimerRef.current)
                                            typingTimerRef.current = setTimeout(
                                                () => setSearch(event.target.value || null),
                                                800
                                            )
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search sx={{ color: '#94A3B8', fontSize: 20 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fff',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Type Chips */}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                {CONTACT_TYPE_OPTIONS.map((opt) => (
                                    <Chip
                                        key={opt.key ?? 'all'}
                                        label={opt.label}
                                        size="small"
                                        onClick={() => {
                                            setActiveType(opt.key)
                                            setFilters((prev) => ({ ...prev, page: 1 }))
                                        }}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            borderRadius: '8px',
                                            height: 32,
                                            px: 0.5,
                                            bgcolor:
                                                activeType === opt.key
                                                    ? `${opt.color}14`
                                                    : 'transparent',
                                            color:
                                                activeType === opt.key ? opt.color : '#64748B',
                                            border:
                                                activeType === opt.key
                                                    ? `1.5px solid ${opt.color}`
                                                    : '1.5px solid #E2E8F0',
                                            '&:hover': {
                                                bgcolor: `${opt.color}14`,
                                                borderColor: opt.color,
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Card>

                    {/* Table Card */}
                    <Card
                        sx={{
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        {response?.results ? (
                            <TableContainer>
                                {response?.results?.length > 0 ? (
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                                <TableCell sx={cellSx}>Nome</TableCell>
                                                <TableCell sx={cellSx}>CPF/CNPJ</TableCell>
                                                <TableCell sx={cellSx}>Email</TableCell>
                                                <TableCell sx={cellSx}>Telefone</TableCell>
                                                <TableCell align="center" sx={cellSx}>
                                                    Tipo
                                                </TableCell>
                                                <TableCell align="center" sx={cellSx}>
                                                    Ações
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {response?.results?.map((contact) => (
                                                <TableRow
                                                    key={contact.id}
                                                    onClick={() => navigate(`/financeiro/contatos/${contact.id}`)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        transition:
                                                            'background-color 0.15s ease',
                                                        '&:hover': { bgcolor: '#F1F5F9' },
                                                        '& td': {
                                                            borderBottom: '1px solid #F1F5F9',
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#1E293B',
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {contact.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#64748B',
                                                                fontFamily: 'monospace',
                                                            }}
                                                        >
                                                            {contact.cpf
                                                                ? formatCpfCnpj(contact.cpf)
                                                                : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ color: '#64748B' }}
                                                        >
                                                            {contact.email || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ color: '#64748B' }}
                                                        >
                                                            {contact.phone
                                                                ? formatPhone(contact.phone)
                                                                : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {getTypeChip(contact.contact_type)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                openEditDialog(contact)
                                                            }}
                                                        >
                                                            <Edit
                                                                sx={{
                                                                    fontSize: 18,
                                                                    color: '#94A3B8',
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <NoResultsAnimation />
                                )}
                            </TableContainer>
                        ) : (
                            <Skeleton
                                variant="rounded"
                                height={500}
                                animation="pulse"
                                sx={{ m: 2 }}
                            />
                        )}
                    </Card>

                    {response?.count > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <PaginationGlobal
                                state={response}
                                filters={filters}
                                setFilters={setFilters}
                            />
                        </Box>
                    )}
                </Container>
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {editMode ? 'Editar Contato' : 'Novo Contato'}
                    </Typography>
                    <IconButton size="small" onClick={() => setDialogOpen(false)}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Nome"
                                size="small"
                                required
                                value={form.name}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, name: e.target.value }))
                                }
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="CPF/CNPJ"
                                size="small"
                                required
                                value={form.cpf}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, cpf: e.target.value }))
                                }
                                error={!!errors.cpf}
                                helperText={errors.cpf}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Telefone"
                                size="small"
                                value={form.phone}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, phone: e.target.value }))
                                }
                                error={!!errors.phone}
                                helperText={errors.phone}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                size="small"
                                value={form.email}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, email: e.target.value }))
                                }
                                error={!!errors.email}
                                helperText={errors.email}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Tipo de Contato"
                                size="small"
                                value={form.contact_type}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        contact_type: e.target.value,
                                    }))
                                }
                            >
                                <MenuItem value="CLIENT">Cliente</MenuItem>
                                <MenuItem value="EMPLOYEE">Funcionário</MenuItem>
                                <MenuItem value="SUPPLIER">Fornecedor</MenuItem>
                                <MenuItem value="BUSINESS_PARTNER">Sócio</MenuItem>
                            </TextField>
                        </Grid>

                        {/* Endereço */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ color: '#94A3B8', mt: 1 }}>
                                Endereço
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="CEP"
                                size="small"
                                value={form.cep}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, cep: e.target.value }))
                                }
                                onBlur={handleCepBlur}
                                disabled={cepLoading}
                                error={!!errors.cep}
                                helperText={errors.cep || (cepLoading ? 'Buscando CEP...' : '')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                label="Rua"
                                size="small"
                                value={form.street}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, street: e.target.value }))
                                }
                                error={!!errors.street}
                                helperText={errors.street}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Número"
                                size="small"
                                value={form.address_number}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, address_number: e.target.value }))
                                }
                                error={!!errors.address_number}
                                helperText={errors.address_number}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                label="Complemento"
                                size="small"
                                value={form.complement}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, complement: e.target.value }))
                                }
                                error={!!errors.complement}
                                helperText={errors.complement}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Bairro"
                                size="small"
                                value={form.district}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, district: e.target.value }))
                                }
                                error={!!errors.district}
                                helperText={errors.district}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Cidade"
                                size="small"
                                value={form.city}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, city: e.target.value }))
                                }
                                error={!!errors.city}
                                helperText={errors.city}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                select
                                label="Estado"
                                size="small"
                                value={form.state}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, state: e.target.value }))
                                }
                                error={!!errors.state}
                                helperText={errors.state}
                            >
                                <MenuItem value="">Selecione</MenuItem>
                                {STATES.map((uf) => (
                                    <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        sx={{ textTransform: 'none', color: '#64748B' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                            bgcolor: '#3B82F6',
                            '&:hover': { bgcolor: '#2563EB' },
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                        }}
                    >
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ContactsList
